<template>
  <div
    class="flex items-center justify-center min-h-screen bg-white dark:bg-black p-4 md:p-8 lg:p-16"
  >
    <div
      class="bg-white dark:bg-black items-center justify-center relative p-6 md:p-8 lg:p-12 w-full max-w-md"
    >
      <div class="space-y-8">
        <div class="space-y-4 mb-8">
          <div class="flex items-center justify-center space-x-2 mb-8 relative">
            <router-link to="/home" class="flex items-center">
              <img src="Logo-PG.svg" alt="Logo" class="h-10 w-auto" />
            </router-link>
          </div>
          <h2
            class="text-3xl font-bold mb-6 text-left text-bodyText dark:text-white"
          >
            Reset Password
          </h2>
          <p class="text-[14px] text-grayDark dark:text-gray-400">
            Enter the code you received in your email and set your new password
          </p>
        </div>
        <form @submit.prevent="resetPassword">
          <div class="mb-4">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Email</label
            >
            <input
              v-model="email"
              type="email"
              placeholder="Enter your email address"
              class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9] dark:bg-[#1A1A1A] dark:text-white"
              required
              :disabled="isLoading"
            />
          </div>
          <div class="mb-4">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Reset Code</label
            >
            <input
              v-model="code"
              type="text"
              placeholder="Enter the 6-digit code"
              class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9] dark:bg-[#1A1A1A] dark:text-white"
              required
              maxlength="6"
              :disabled="isLoading"
            />
          </div>
          <div class="mb-4">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >New Password</label
            >
            <div class="relative">
              <input
                v-model="newPassword"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your new password"
                class="mt-1 block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9] dark:bg-[#1A1A1A] dark:text-white"
                required
                :disabled="isLoading"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span @click="togglePassword" class="cursor-pointer">
                  <svg
                    v-if="!showPassword"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-gray-900 dark:text-gray-100"
                  >
                    <title>view</title>
                    <g
                      stroke-linejoin="miter"
                      fill="none"
                      stroke="currentColor"
                      stroke-linecap="butt"
                      class="nc-icon-wrapper"
                    >
                      <path
                        d="M1.373,13.183a2.064,2.064,0,0,1,0-2.366C2.946,8.59,6.819,4,12,4s9.054,4.59,10.627,6.817a2.064,2.064,0,0,1,0,2.366C21.054,15.41,17.181,20,12,20S2.946,15.41,1.373,13.183Z"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <circle
                        data-color="color-2"
                        cx="12"
                        cy="12"
                        r="4"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></circle>
                    </g>
                  </svg>
                  <svg
                    v-else
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-gray-900 dark:text-gray-100"
                  >
                    <title>view off</title>
                    <g
                      stroke-linejoin="miter"
                      fill="none"
                      stroke="currentColor"
                      stroke-linecap="butt"
                      class="nc-icon-wrapper"
                    >
                      <path
                        d="M3,3L21,21"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <path
                        d="M10.584,5.75A8.779,8.779,0,0,1,12,5.5c5.182,0,9.054,4.59,10.627,6.817a2.064,2.064,0,0,1,0,2.366,15.4,15.4,0,0,1-1.778,2.075"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <path
                        d="M6.982,6.982C3.919,8.944,2.127,11.756,1.373,13.183a2.064,2.064,0,0,0,0,2.366C2.946,17.776,6.819,22.366,12,22.366a9.158,9.158,0,0,0,4.018-.982"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <path
                        d="M8.465,8.465a3.993,3.993,0,0,0,7.07,7.07"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                    </g>
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Confirm New Password</label
            >
            <div class="relative">
              <input
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirm your new password"
                class="mt-1 block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9] dark:bg-[#1A1A1A] dark:text-white"
                required
                :disabled="isLoading"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span @click="toggleConfirmPassword" class="cursor-pointer">
                  <svg
                    v-if="!showConfirmPassword"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-gray-900 dark:text-gray-100"
                  >
                    <title>view</title>
                    <g
                      stroke-linejoin="miter"
                      fill="none"
                      stroke="currentColor"
                      stroke-linecap="butt"
                      class="nc-icon-wrapper"
                    >
                      <path
                        d="M1.373,13.183a2.064,2.064,0,0,1,0-2.366C2.946,8.59,6.819,4,12,4s9.054,4.59,10.627,6.817a2.064,2.064,0,0,1,0,2.366C21.054,15.41,17.181,20,12,20S2.946,15.41,1.373,13.183Z"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <circle
                        data-color="color-2"
                        cx="12"
                        cy="12"
                        r="4"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></circle>
                    </g>
                  </svg>
                  <svg
                    v-else
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-gray-900 dark:text-gray-100"
                  >
                    <title>view off</title>
                    <g
                      stroke-linejoin="miter"
                      fill="none"
                      stroke="currentColor"
                      stroke-linecap="butt"
                      class="nc-icon-wrapper"
                    >
                      <path
                        d="M3,3L21,21"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <path
                        d="M10.584,5.75A8.779,8.779,0,0,1,12,5.5c5.182,0,9.054,4.59,10.627,6.817a2.064,2.064,0,0,1,0,2.366,15.4,15.4,0,0,1-1.778,2.075"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <path
                        d="M6.982,6.982C3.919,8.944,2.127,11.756,1.373,13.183a2.064,2.064,0,0,0,0,2.366C2.946,17.776,6.819,22.366,12,22.366a9.158,9.158,0,0,0,4.018-.982"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                      <path
                        d="M8.465,8.465a3.993,3.993,0,0,0,7.07,7.07"
                        stroke-linecap="square"
                        stroke-miterlimit="10"
                        stroke-width="2"
                      ></path>
                    </g>
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <button
            type="submit"
            class="w-full py-2 px-4 bg-primaryDarkBg hover:bg-primaryDark text-white font-bold rounded disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isLoading"
          >
            <span v-if="!isLoading">Reset Password</span>
            <span v-else>Resetting...</span>
          </button>
        </form>
      </div>

      <div>
        <hr
          class="w-full border-t-[1px] border-grayLight dark:border-gray-800 mb-6"
        />
        <p
          class="mt-6 text-primaryDark dark:text-white flex justify-between items-center text-sm"
        >
          Remember your password?
          <router-link
            to="/login"
            class="text-primaryDark dark:text-blue-400 hover:underline px-3 py-3 bg-[#EEEEEE] dark:bg-[#1A1A1A] rounded-md transition-colors"
            >Log In</router-link
          >
        </p>
        <p
          class="mt-4 text-primaryDark dark:text-white flex justify-between items-center text-sm"
        >
          Didn't receive a code?
          <router-link
            to="/forgot-password"
            class="text-primaryDark dark:text-blue-400 hover:underline px-3 py-3 bg-[#EEEEEE] dark:bg-[#1A1A1A] rounded-md transition-colors"
            >Resend Code</router-link
          >
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";

export default {
  name: "ResetPasswordComponent",
  data() {
    return {
      email: "",
      code: "",
      newPassword: "",
      confirmPassword: "",
      isLoading: false,
      showPassword: false,
      showConfirmPassword: false,
      showLogoDropdown: false,
    };
  },
  created() {
    if (this.$route.params.email) {
      this.email = this.$route.params.email;
    } else if (this.$route.query.email) {
      this.email = this.$route.query.email;
    }

    if (this.$route.query.code) {
      this.code = this.$route.query.code;
    }
  },
  methods: {
    toggleLogoDropdown() {
      this.showLogoDropdown = !this.showLogoDropdown;
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    toggleConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    async resetPassword() {
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.email) {
        this.errorMessage = "Please enter your email address";
        return;
      }

      if (!this.code) {
        this.$toast.error("Validation Error", "Please enter the reset code");
        return;
      }

      if (!this.newPassword) {
        this.$toast.error("Validation Error", "Please enter your new password");
        return;
      }

      if (!this.confirmPassword) {
        this.$toast.error(
          "Validation Error",
          "Please confirm your new password",
        );
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.$toast.error(
          "Validation Error",
          "Please enter a valid email address",
        );
        return;
      }

      if (!/^\d{6}$/.test(this.code)) {
        this.$toast.error("Validation Error", "Reset code must be 6 digits");
        return;
      }

      if (this.newPassword.length < 8) {
        this.$toast.error(
          "Validation Error",
          "Password must be at least 8 characters long",
        );
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        this.$toast.error("Validation Error", "Passwords do not match");
        return;
      }

      this.isLoading = true;

      try {
        const response = await apiClient.post("/web/reset_password", {
          email: this.email,
          code: this.code,
          new_password: this.newPassword,
        });

        if (response.data.status_code === 200) {
          this.$toast.success("Password Reset", response.data.message);
          this.email = "";
          this.code = "";
          this.newPassword = "";
          this.confirmPassword = "";

          setTimeout(() => {
            this.$router.push({
              name: "Login",
              query: {
                message:
                  "Password reset successfully. Please log in with your new password.",
              },
            });
          }, 2000);
        }
      } catch (error) {
        console.error("Error resetting password:", error);

        const errorMsg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "An error occurred. Please try again later.";
        this.$toast.error("Reset Failed", errorMsg);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Autofill styling for dark mode */
.dark input:-webkit-autofill,
.dark input:-webkit-autofill:hover,
.dark input:-webkit-autofill:focus,
.dark textarea:-webkit-autofill,
.dark textarea:-webkit-autofill:hover,
.dark textarea:-webkit-autofill:focus,
.dark select:-webkit-autofill,
.dark select:-webkit-autofill:hover,
.dark select:-webkit-autofill:focus {
  -webkit-text-fill-color: white !important;
  -webkit-box-shadow: 0 0 0px 1000px #0a0a0a inset !important;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
